import { DecimalPipe } from '@angular/common';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { formatWorkingTime, HEALTH, SimulationReport, Suggestion } from './simulation-report';

const HEALTH_LABELS: Record<HEALTH, string> = {
    starting: 'Getting going',
    good: 'Running smoothly',
    warning: 'Some work waiting',
    poor: 'Work backing up',
};

// a simulation's results over the graph: a short summary while it runs, and the full results, with what to change,
// once it stops. Steps and outputs can be clicked to find them in the graph
@Component({
    selector: 'app-simulation-results',
    standalone: true,
    imports: [DecimalPipe, MatButtonModule, MatIconModule, MatTooltipModule],
    template: `
        @if (report; as r) {
        <section class="results" [class.full]="full" [class.minimised]="minimised && !full" role="region" aria-label="Simulation results" data-testid="simulation-results">
            <header>
                <span class="health" [class]="'health ' + r.health" data-testid="simulation-health">{{ healthLabel(r) }}</span>
                <span class="when">Week {{ r.week }}, day {{ r.day }}</span>
                <span class="spacer"></span>
                @if (!full) {
                <button mat-icon-button (click)="minimised = !minimised" [attr.aria-label]="minimised ? 'Show the results' : 'Hide the results'"
                    [matTooltip]="minimised ? 'Show the results' : 'Hide the results'">
                    <mat-icon>{{ minimised ? 'expand_more' : 'expand_less' }}</mat-icon>
                </button>
                }@else {
                <button mat-icon-button (click)="closed.emit()" aria-label="Close the results" matTooltip="Close the results" data-testid="close-simulation-results">
                    <mat-icon>close</mat-icon>
                </button>
                }
            </header>

            @if (!minimised || full) {
            <p class="headline">{{ r.headline }}</p>
            <div class="figures">
                <div><strong>{{ r.received | number }}</strong><span>items in</span></div>
                <div><strong>{{ r.produced | number }}</strong><span>outputs produced{{ r.expected ? ' of ' + (r.expected | number) + ' expected' : '' }}</span></div>
                <div><strong>{{ r.queued | number }}</strong><span>waiting at steps</span></div>
            </div>

            @if (full && r.health !== 'starting') {
            <div class="recommend">
                <button mat-flat-button color="primary" (click)="recommend.emit()" [disabled]="!canRecommend" data-testid="recommend"
                    [matTooltip]="canRecommend ? 'Recommendations from AI, from the EPM scorecard and this simulation' : testDisabledReason">
                    <mat-icon>auto_awesome</mat-icon>Recommendations
                </button>
                <span>Combines the EPM scorecard with this simulation, including where AI could help.</span>
            </div>
            }

            @if (full && r.suggestions.length) {
            <div class="change-header">
                <h4>What to change</h4>
                @if (testable(r)) {
                <button mat-stroked-button color="accent" (click)="testChanges.emit(testable(r))" data-testid="test-suggestions"
                    [disabled]="!canTest" [matTooltip]="canTest ? 'Try these changes in a draft project and compare it with the current state' : testDisabledReason">
                    <mat-icon>science</mat-icon>Test in a draft project
                </button>
                }
            </div>
            <ul class="suggestions" data-testid="simulation-suggestions">
                @for (s of r.suggestions; track $index) {
                <li>
                    <mat-icon>lightbulb</mat-icon>
                    <span>{{ s.text }}</span>
                    @if (s.stepKey) {
                    <button mat-icon-button class="find" (click)="stepSelected.emit(s.stepKey)" aria-label="Show the step" matTooltip="Show the step"><mat-icon>my_location</mat-icon></button>
                    }
                </li>
                }
            </ul>
            }

            @if (full && r.roles.length) {
            <h4>Roles that need more capacity</h4>
            <table data-testid="simulation-roles">
                <thead><tr><th>Role</th><th class="num">Work vs capacity</th><th class="num">Short by</th><th>Holds up</th></tr></thead>
                <tbody>
                    @for (role of r.roles; track role.key) {
                    <tr>
                        <td>{{ role.name }}</td>
                        <td class="num">{{ role.utilisation ? (role.utilisation * 100 | number: '1.0-0') + '%' : '–' }}</td>
                        <td class="num">{{ role.extraHours ? (role.extraHours | number) + ' hours a year' : role.extraPeople + ' at once' }}</td>
                        <td>
                            @for (step of role.steps; track step.key; let last = $last) {
                            <a (click)="stepSelected.emit(step.key)">{{ step.name }}</a>{{ last ? '' : ', ' }}
                            }
                        </td>
                    </tr>
                    }
                </tbody>
            </table>
            }

            <h4>{{ full ? 'Steps where work waits' : 'Queues building' }}</h4>
            @if (r.bottlenecks.length) {
            <table data-testid="simulation-bottlenecks">
                <thead><tr><th>Step</th><th class="num">Waiting</th><th class="num">Wait</th>@if (full) {<th>Why</th>}</tr></thead>
                <tbody>
                    @for (b of (full ? r.bottlenecks : r.bottlenecks.slice(0, 4)); track b.key) {
                    <tr>
                        <td><a (click)="stepSelected.emit(b.key)" [matTooltip]="b.model">{{ b.name }}</a></td>
                        <td class="num">{{ b.queue | number }}@if (b.growthPerDay >= 1) {<span class="growth">+{{ b.growthPerDay | number: '1.0-0' }}/day</span>}</td>
                        <td class="num">{{ time(b.waitSeconds) }}</td>
                        @if (full) {<td class="why">{{ b.explanation }}</td>}
                    </tr>
                    }
                </tbody>
            </table>
            @if (!full && r.bottlenecks.length > 4) {<p class="more">and {{ r.bottlenecks.length - 4 }} more</p>}
            }@else {
            <p class="none">{{ r.health === 'starting' ? 'Too early to tell.' : 'No lasting queues.' }}</p>
            }

            <h4>Outputs affected</h4>
            @if (r.outputs.length) {
            <table data-testid="simulation-outputs">
                <thead><tr><th>Output</th><th class="num">Produced</th><th class="num">Delay</th>@if (full) {<th>Why</th>}</tr></thead>
                <tbody>
                    @for (o of (full ? r.outputs : r.outputs.slice(0, 3)); track o.key) {
                    <tr>
                        <td><a (click)="stepSelected.emit(o.key)" [matTooltip]="o.model">{{ o.name }}</a></td>
                        <td class="num">{{ o.produced | number }}{{ o.expected ? ' of ' + (o.expected | number) : '' }}</td>
                        <td class="num">{{ o.delaySeconds ? time(o.delaySeconds) : '–' }}</td>
                        @if (full) {
                        <td class="why">
                            @if (o.heldUpBy.length) {
                            Held up by
                            @for (step of o.heldUpBy; track step.key; let last = $last) {
                            <a (click)="stepSelected.emit(step.key)">{{ step.name }}</a>{{ last ? '' : ', ' }}
                            }
                            }@else {
                            <span>{{ o.reason || '–' }}</span>
                            }
                        </td>
                        }
                    </tr>
                    }
                </tbody>
            </table>
            @if (!full && r.outputs.length > 3) {<p class="more">and {{ r.outputs.length - 3 }} more</p>}
            }@else {
            <p class="none">{{ r.health === 'starting' ? 'Too early to tell.' : 'Outputs are on track.' }}</p>
            }
            }
        </section>
        }
    `,
    styles: [`
        .results { position: absolute; top: 12px; right: 12px; z-index: 5; width: min(360px, calc(100% - 24px)); max-height: calc(100% - 24px);
            overflow: auto; box-sizing: border-box; padding: 10px 14px 12px; background: var(--mat-sys-surface, #fff); color: var(--mat-sys-on-surface, #1f2937);
            border: 1px solid rgba(0,0,0,.12); border-radius: 10px; box-shadow: 0 6px 20px rgba(0,0,0,.14); font-size: 13px; }
        .results.full { width: min(760px, calc(100% - 24px)); }
        .results.minimised { padding-bottom: 6px; }
        header { display: flex; align-items: center; gap: 8px; min-height: 32px; }
        .spacer { flex: 1; }
        .when { color: var(--slate, #5b6172); }
        .health { padding: 2px 10px; border-radius: 12px; font-weight: 600; font-size: 12px; white-space: nowrap; }
        .health.good { background: #dcfce7; color: #166534; }
        .health.warning { background: #fef3c7; color: #92400e; }
        .health.poor { background: #fee2e2; color: #991b1b; }
        .health.starting { background: #e5e7eb; color: #374151; }
        .headline { margin: 6px 0 8px; }
        .figures { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 8px; margin-bottom: 4px; }
        .figures div { display: flex; flex-direction: column; min-width: 0; }
        .figures strong { font-size: 16px; }
        .figures span { color: var(--slate, #5b6172); font-size: 12px; }
        h4 { margin: 12px 0 4px; font-size: 13px; }
        .recommend { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; margin: 10px 0 2px; }
        .recommend span { color: var(--slate, #5b6172); font-size: 12px; flex: 1; min-width: 160px; }
        .change-header { display: flex; align-items: center; justify-content: space-between; gap: 8px; flex-wrap: wrap; }
        .change-header button { margin-top: 8px; }
        table { width: 100%; border-collapse: collapse; table-layout: auto; }
        th { text-align: left; font-weight: 600; color: var(--slate, #5b6172); font-size: 12px; padding: 2px 4px; }
        td { padding: 3px 4px; border-top: 1px solid rgba(0,0,0,.08); vertical-align: top; overflow-wrap: anywhere; }
        .num { text-align: right; white-space: nowrap; }
        .why { color: var(--slate, #5b6172); }
        .growth { display: block; color: #b91c1c; font-size: 11px; }
        a { cursor: pointer; color: var(--mat-sys-primary, #0f766e); }
        a:hover { text-decoration: underline; }
        .none, .more { margin: 2px 0; color: var(--slate, #5b6172); }
        .suggestions { list-style: none; margin: 0; padding: 0; }
        .suggestions li { display: flex; align-items: flex-start; gap: 6px; padding: 4px 0; border-top: 1px solid rgba(0,0,0,.08); }
        .suggestions li span { flex: 1; }
        .suggestions mat-icon { color: #d97706; font-size: 18px; width: 18px; height: 18px; flex: none; }
        .suggestions .find { margin: -8px -8px -8px 0; }
        .suggestions .find mat-icon { color: inherit; }
    `],
})
export class SimulationResultsComponent {
    @Input() report?: SimulationReport;
    @Input() full = false;          // the full results, once the simulation stops
    @Output() stepSelected = new EventEmitter<string>();
    @Output() closed = new EventEmitter<void>();
    @Input() canTest = true;                                // suggestions can be tried in a draft from here
    @Input() testDisabledReason = '';
    @Output() testChanges = new EventEmitter<Suggestion[]>();    // the suggestions that can be tried
    @Input() canRecommend = true;                           // recommendations can be asked for from here
    @Output() recommend = new EventEmitter<void>();

    minimised = false;

    // the suggestions that come with a change to the model, which can be tried in a draft
    testable(r: SimulationReport) {
        const suggestions = r.suggestions.filter(s => s.change);
        return suggestions.length ? suggestions : undefined;
    }

    time(seconds: number) {
        return formatWorkingTime(seconds);
    }

    healthLabel(r: SimulationReport) {
        return HEALTH_LABELS[r.health];
    }
}
